import type { ReactElement, ReactNode } from 'react';
import { App, Button, Dropdown, Space, Tooltip, Typography } from 'antd';
import type { ButtonProps, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import {
  copyAdminLink,
  openWithAdminToken,
  SHARE_LINK_DURATIONS,
} from '@/utils/adminPreview';

interface ShareLinkButtonProps {
  /** client-fluffy path, e.g. `/book/preview/<orderId>` */
  path: string;
  label?: string;
  icon: ReactNode;
  tooltip?: string;
  size?: ButtonProps['size'];
  type?: ButtonProps['type'];
}

const MENU_ITEMS: MenuProps['items'] = SHARE_LINK_DURATIONS.map((duration) => ({
  key: duration.key,
  label: `Copy link · ${duration.label}`,
}));

/**
 * Opens a client-fluffy page with a fresh admin token (the main button), or
 * copies a link that stays valid for 1 hour / 1 day / 7 days (the dropdown).
 *
 * 🚨 A copied link IS a full admin token, not scoped to this order: anyone
 * holding it can use it as the admin on any order until it expires.
 */
export function ShareLinkButton({
  path,
  label,
  icon,
  tooltip,
  size,
  type,
}: ShareLinkButtonProps): ReactElement {
  const { message, modal } = App.useApp();

  const mainButton = (
    <Button
      type={type}
      size={size}
      icon={icon}
      onClick={() => openWithAdminToken(path)}
    >
      {label}
    </Button>
  );

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const duration = SHARE_LINK_DURATIONS.find((d) => d.key === key);
    if (!duration) return;

    // No await before this call: Safari needs the clipboard write to start
    // inside the click's user activation (see copyAdminLink).
    copyAdminLink(path, duration.minutes).then(
      (result) => {
        if (result === 'copied') {
          message.success(`Link copied — expires in ${duration.label}`);
          return;
        }
        modal.info({
          title: `Link (expires in ${duration.label})`,
          content: (
            <Typography.Paragraph copyable style={{ wordBreak: 'break-all' }}>
              {result.manualUrl}
            </Typography.Paragraph>
          ),
        });
      },
      () => {
        message.error('Could not create the link');
      }
    );
  };

  return (
    <Space.Compact size={size}>
      {tooltip ? <Tooltip title={tooltip}>{mainButton}</Tooltip> : mainButton}
      <Dropdown
        trigger={['click']}
        menu={{ items: MENU_ITEMS, onClick: handleMenuClick }}
      >
        <Button
          type={type}
          size={size}
          icon={<DownOutlined />}
          aria-label="Copy link"
        />
      </Dropdown>
    </Space.Compact>
  );
}
